// Reusable Q&A control. Parses <question label="..."><option>...</option></question>
// blocks out of an AI message and renders them as clickable buttons with an
// "Other..." free-text fallback — the same pattern the workflow planner uses.
//
// Usage:
//   var qc = require('./QuestionControl');
//   var parsed = qc.parseQuestionTags(text);
//     // → { parts: [{type:'text', text}|{type:'question', question, options}|{type:'input', question}] }
//
//   <QuestionControl parts={parsed.parts} interactive={isLast} onSubmit={handleSend} />
//
// The control owns its own selection state. When the user clicks "Submit
// Answers" it calls onSubmit(combinedAnswerText) where combinedAnswerText is
// "<question>: <answer>\n<question2>: <answer2>" — the format the planner
// service feeds back into the model so it has context for what was answered.
//
// Styling reuses the existing `planner-msg-*` classes in WorkflowCanvas.css
// so the doctor chat and any future caller look identical to the planner.

var React = require('react');
var { createElement: h, useState } = React;
// Multi-select answers are arrays; single ones are strings. Shared with the
// planner surfaces so there is one definition of "what is selected".
var { selectionList, selectionText } = require('../lib/plannerChatFormat');

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------

function parseOptions(optionsBlock) {
  var options = [];
  var optPattern = /<option>([\s\S]*?)<\/option>/gi;
  var optMatch;
  while ((optMatch = optPattern.exec(optionsBlock)) !== null) {
    var optText = optMatch[1].trim();
    if (optText) options.push(optText);
  }
  return options;
}

function parseQuestionTags(content) {
  if (!content || typeof content !== 'string') {
    return { parts: [{ type: 'text', text: content || '' }] };
  }

  var parts = [];
  var pattern = /<question\s+([^>]*?)>([\s\S]*?)<\/question>/gi;
  var lastIndex = 0;
  var match;

  while ((match = pattern.exec(content)) !== null) {
    var before = content.slice(lastIndex, match.index).trim();
    if (before) parts.push({ type: 'text', text: before });

    // label="..." plus the optional multi="true" flag (several answers apply).
    var attrs = match[1] || '';
    var labelAttr = attrs.match(/label\s*=\s*"([^"]*)"/i);
    var label = labelAttr ? labelAttr[1].trim() : '';
    var isMulti = /\bmulti\s*=\s*"\s*(?:true|yes|1)\s*"/i.test(attrs);
    var options = parseOptions(match[2]);
    if (options.length > 0) {
      parts.push({ type: 'question', question: label, options: options, multi: isMulti });
    } else {
      parts.push({ type: 'input', question: label });
    }
    lastIndex = match.index + match[0].length;
  }

  var remaining = content.slice(lastIndex).trim();
  if (remaining) parts.push({ type: 'text', text: remaining });
  if (parts.length === 0) parts.push({ type: 'text', text: content });
  return { parts: parts };
}

// ---------------------------------------------------------------------------
// Sub-renderers
// ---------------------------------------------------------------------------

function RadioDot() {
  return h('span', { className: 'planner-msg-radio' },
    h('span', { className: 'planner-msg-radio-outer' },
      h('span', { className: 'planner-msg-radio-inner' })
    )
  );
}

function SelectedAnswer(props) {
  return h('div', { className: 'planner-msg-selected-answer' },
    h('div', { className: 'planner-msg-selected-value' },
      h('span', { className: 'planner-msg-radio planner-msg-radio-selected' },
        h('span', { className: 'planner-msg-radio-outer' },
          h('span', { className: 'planner-msg-radio-inner' })
        )
      ),
      h('span', null, props.value)
    ),
    h('button', {
      className: 'planner-msg-change-btn',
      onClick: props.onChange
    }, 'Change')
  );
}

function OtherInput(props) {
  return h('div', { className: 'planner-msg-other-row' },
    h('input', {
      type: 'text',
      className: 'planner-msg-other-field',
      placeholder: 'Type your answer...',
      value: props.value,
      onChange: function(e) { props.onChange(e.target.value); },
      onKeyDown: function(e) {
        if (e.key === 'Enter' && props.value.trim()) {
          e.preventDefault();
          props.onSubmit();
        } else if (e.key === 'Escape') {
          props.onCancel();
        }
      },
      autoFocus: true
    }),
    h('button', {
      className: 'planner-msg-other-submit',
      onClick: props.onSubmit,
      disabled: !props.value.trim()
    }, 'OK')
  );
}

// A checkbox replaces the radio dot on multi-select questions, so the row
// itself shows whether it is one-of or many-of.
function CheckBox(props) {
  return h('span', { className: 'planner-msg-option-check' + (props.checked ? ' planner-msg-option-check-on' : '') },
    props.checked
      ? h('svg', { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 3 },
          h('polyline', { points: '20 6 9 17 4 12' })
        )
      : null
  );
}

function OptionButtons(props) {
  var qIdx = props.qIdx;
  var options = props.options;
  var multi = props.multi;
  var chosen = props.chosen || [];
  var otherOpen = props.otherOpen;
  var otherText = props.otherText;
  var onPick = props.onPick;
  var onOpenOther = props.onOpenOther;
  var onOtherChange = props.onOtherChange;
  var onOtherSubmit = props.onOtherSubmit;
  var onOtherCancel = props.onOtherCancel;

  return h('div', { className: 'planner-msg-options' },
    options.map(function(opt, oIdx) {
      var isSel = multi && chosen.indexOf(opt) !== -1;
      return h('button', {
        key: oIdx,
        className: 'planner-msg-option-btn' + (isSel ? ' planner-msg-option-selected' : ''),
        onClick: function() { onPick(opt); }
      }, multi ? h(CheckBox, { checked: isSel }) : h(RadioDot), h('span', null, opt));
    }),
    otherOpen === qIdx
      ? h(OtherInput, {
          value: otherText,
          onChange: onOtherChange,
          onSubmit: onOtherSubmit,
          onCancel: onOtherCancel
        })
      : h('button', {
          className: 'planner-msg-option-btn planner-msg-option-other',
          onClick: function() { onOpenOther(qIdx); }
        }, multi ? h(CheckBox, { checked: false }) : h(RadioDot), h('span', null, 'Other...'))
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
//
// Props:
//   parts        — output from parseQuestionTags(text).parts
//   interactive  — true on the latest assistant message AND when not busy
//   renderText   — optional fn(text) → ReactNode for the plain text segments
//   onSubmit     — called with the combined answer string when user submits
//
// Each `question` part has its own index. Selections are keyed by index so
// the user can fill questions out in any order. Submit appends "Q: A" lines
// for every answered question.

function QuestionControl(props) {
  var parts = Array.isArray(props.parts) ? props.parts : [];
  var interactive = !!props.interactive;
  var renderText = typeof props.renderText === 'function'
    ? props.renderText
    : function(t) { return t; };
  var onSubmit = props.onSubmit || function() {};

  var [selections, setSelections] = useState({});
  var [otherOpen, setOtherOpen] = useState(null);
  var [otherText, setOtherText] = useState('');

  var questions = parts.filter(function(p) {
    return p.type === 'question' || p.type === 'input';
  });
  var hasQuestions = questions.length > 0;

  // On a multi-select question a pick TOGGLES membership and the answer stays
  // an array; everywhere else it replaces the single answer.
  function pick(qIdx, value, multi) {
    var copy = Object.assign({}, selections);
    if (multi) {
      var chosen = selectionList(copy[qIdx]);
      copy[qIdx] = chosen.indexOf(value) !== -1
        ? chosen.filter(function(v) { return v !== value; })
        : chosen.concat([value]);
      if (copy[qIdx].length === 0) delete copy[qIdx];
    } else {
      copy[qIdx] = value;
    }
    setSelections(copy);
    setOtherOpen(null);
    setOtherText('');
  }

  function clearOne(qIdx) {
    var copy = Object.assign({}, selections);
    delete copy[qIdx];
    setSelections(copy);
  }

  function commitOther() {
    if (otherOpen == null) return;
    var trimmed = (otherText || '').trim();
    if (!trimmed) return;
    var q = questions[otherOpen];
    pick(otherOpen, trimmed, !!(q && q.multi));
  }

  function submit() {
    var lines = [];
    for (var i = 0; i < questions.length; i++) {
      var q = questions[i];
      // Multi-select answers arrive as an array — selectionText joins them.
      var ans = selectionText(selections[i]);
      if (ans) {
        lines.push(q.question + ': ' + ans);
      }
    }
    if (lines.length === 0) return;
    setSelections({});
    setOtherOpen(null);
    setOtherText('');
    onSubmit(lines.join('\n'));
  }

  // Walk parts and emit elements. qIdx tracks which question we're on.
  var qIdx = 0;
  var elements = parts.map(function(part, pIdx) {
    if (part.type === 'text') {
      return h('div', { key: 'txt-' + pIdx, className: 'planner-message-text' },
        renderText(part.text)
      );
    }

    if (part.type === 'question') {
      var thisQ = qIdx++;
      var isMultiQ = !!part.multi;
      var chosenQ = selectionList(selections[thisQ]);

      if (!interactive) {
        return h('div', { key: 'q-' + pIdx, className: 'planner-msg-question-block' },
          h('div', { className: 'planner-msg-question-label' }, part.question),
          h('div', { className: 'planner-msg-options-static' },
            part.options.map(function(opt, oIdx) {
              return h('div', { key: oIdx, className: 'planner-msg-option-static' }, '• ' + opt);
            })
          )
        );
      }

      // Multi-select keeps every row on screen so more boxes can be ticked;
      // a single answer collapses to "<answer> · Change".
      var body = (!isMultiQ && chosenQ.length > 0)
        ? h(SelectedAnswer, {
            value: chosenQ[0],
            onChange: function() { clearOne(thisQ); }
          })
        : h(OptionButtons, {
            qIdx: thisQ,
            options: part.options,
            multi: isMultiQ,
            chosen: chosenQ,
            otherOpen: otherOpen,
            otherText: otherText,
            onPick: function(opt) { pick(thisQ, opt, isMultiQ); },
            onOpenOther: function(i) { setOtherOpen(i); setOtherText(''); },
            onOtherChange: setOtherText,
            onOtherSubmit: commitOther,
            onOtherCancel: function() { setOtherOpen(null); setOtherText(''); }
          });

      return h('div', { key: 'q-' + pIdx, className: 'planner-msg-question-block' },
        h('div', { className: 'planner-msg-question-label' }, part.question),
        isMultiQ ? h('div', { className: 'planner-msg-question-hint' }, 'Select all that apply') : null,
        body
      );
    }

    // type === 'input' — open-ended question, render textarea
    var thisI = qIdx++;
    var current = selections[thisI] || '';
    if (!interactive) {
      return h('div', { key: 'q-' + pIdx, className: 'planner-msg-question-block' },
        h('div', { className: 'planner-msg-question-label' }, part.question),
        current ? h('div', { className: 'planner-msg-input-static' }, current) : null
      );
    }
    return h('div', { key: 'q-' + pIdx, className: 'planner-msg-question-block' },
      h('div', { className: 'planner-msg-question-label' }, part.question),
      h('textarea', {
        className: 'planner-msg-input-textarea',
        placeholder: 'Type your answer...',
        value: current,
        rows: 2,
        onChange: function(e) {
          var copy = Object.assign({}, selections);
          copy[thisI] = e.target.value;
          setSelections(copy);
        }
      })
    );
  });

  if (interactive && hasQuestions) {
    var answeredCount = Object.keys(selections).filter(function(k) {
      return selections[k] && String(selections[k]).trim();
    }).length;
    if (answeredCount > 0) {
      elements.push(
        h('div', { key: 'submit-row', className: 'planner-msg-submit-row' },
          h('span', { className: 'planner-msg-submit-count' },
            answeredCount + ' of ' + questions.length + ' answered'
          ),
          h('button', {
            className: 'planner-msg-submit-btn',
            onClick: submit
          }, 'Submit Answers',
            h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
              h('line', { x1: 22, y1: 2, x2: 11, y2: 13 }),
              h('polygon', { points: '22 2 15 22 11 13 2 9 22 2' })
            )
          )
        )
      );
    }
  }

  return h('div', null, elements);
}

module.exports = {
  parseQuestionTags: parseQuestionTags,
  QuestionControl: QuestionControl
};
